import { AdsAssistantPanel } from "@/components/ads-assistant/AdsAssistantPanel";
import { AdsGuidancePanel } from "@/components/ads-guidance/AdsGuidancePanel";
import { AudienceDataPanel } from "@/components/audience-data/AudienceDataPanel";
import { CoverageAssistantPanel } from "@/components/coverage/CoverageAssistantPanel";
import { ManufacturingInventoryPanel } from "@/components/manufacturing/ManufacturingInventoryPanel";
import { ManufacturingModelsPanel } from "@/components/manufacturing/ManufacturingModelsPanel";
import { ManufacturingPricingGridPanel } from "@/components/manufacturing/pricing/ManufacturingPricingGridPanel";
import { ManufacturingOverheadBucketsPanel } from "@/components/manufacturing/overhead/ManufacturingOverheadBucketsPanel";
import { ManufacturingOrdersPanel } from "@/components/manufacturing/ManufacturingOrdersPanel";
import { SalesIntakePanel } from "@/components/sales-intake/SalesIntakePanel";
import { DealerPlaceOrderPanel } from "@/components/dealer/DealerPlaceOrderPanel";
import { DealerOrderHistoryPanel } from "@/components/dealer/DealerOrderHistoryPanel";
import { OpportunitiesModePanel } from "@/components/opportunity/OpportunitiesModePanel";
import { ManufacturingPlantsPanel } from "@/components/manufacturing/ManufacturingPlantsPanel";
import { ManufacturingBuildPipelinePanel } from "@/components/manufacturing/ManufacturingBuildPipelinePanel";
import { ManufacturingLaborBillingPanel } from "@/components/manufacturing/labor/ManufacturingLaborBillingPanel";
import { ManufacturingFulfillPanel } from "@/components/manufacturing/ManufacturingFulfillPanel";
import { ManufacturingInventoryCountPanel } from "@/components/manufacturing/ManufacturingInventoryCountPanel";
import { ManufacturingPurchasingPanel } from "@/components/manufacturing/ManufacturingPurchasingPanel";
import { BuilderAssistantPanel } from "@/components/manufacturing/builderAssistant/BuilderAssistantPanel";
import { QcDashboardPanel } from "@/components/manufacturing/qc/QcDashboardPanel";
import { ManufacturingLaborPayModePanel } from "@/components/manufacturing/ManufacturingLaborPayModePanel";
import { ManufacturingAuditsPanel, preferMfgAuditsBomLane } from "@/components/manufacturing/ManufacturingAuditsPanel";
import { ManufacturingReportsPanel } from "@/components/manufacturing/ManufacturingReportsPanel";

import { ManufacturingAdminHomePanel } from "@/components/manufacturing/ManufacturingAdminHomePanel";
import { LogisticsRoutesPanel } from "@/components/logistics/LogisticsRoutesPanel";
import { LogisticsCalendarPanel } from "@/components/logistics/LogisticsCalendarPanel";
import { LogisticsCarriersPanel } from "@/components/logistics/LogisticsCarriersPanel";
import { FulfillmentInventoryPanel } from "@/components/fulfillment/FulfillmentInventoryPanel";
import { FulfillmentOrdersPanel } from "@/components/fulfillment/FulfillmentOrdersPanel";
import { FulfillmentIntakePanel } from "@/components/fulfillment/FulfillmentIntakePanel";
import { FulfillmentEntryPanel } from "@/components/fulfillment/FulfillmentEntryPanel";
import { FulfillmentFormsPanel } from "@/components/fulfillment/FulfillmentFormsPanel";
import { ServiceBoardPanel } from "@/components/service/ServiceBoardPanel";
import { ServiceRequestsPanel } from "@/components/service/ServiceRequestsPanel";
import { ServiceSchedulingPanel } from "@/components/service/ServiceSchedulingPanel";
import { ServiceDefinitionsPanel } from "@/components/service/ServiceDefinitionsPanel";
import { ServiceMaterialsUsagePanel } from "@/components/service/ServiceMaterialsUsagePanel";
import { AdminPanel } from "@/components/admin/AdminPanel";
import { OrgDashboard } from "@/components/dashboard/OrgDashboard";
import { DevPanel } from "@/components/dev/DevPanel";
import { KbAdminPanel } from "@/components/KbAdminPanel";
import { LocationToolsPanel } from "@/components/location-tools/LocationToolsPanel";
import { renderLeadWorkspaceOpsMode } from "@/components/lead/LeadWorkspaceShellOpsModes";
import { AiPromptingPanel } from "@/components/platform/AiPromptingPanel";
import { PlatformPanel } from "@/components/platform/PlatformPanel";
import { TrainingAuthorPanel } from "@/components/training-author/TrainingAuthorPanel";
import { FormsAndFormatsModePanel } from "@/components/proposal-formats/FormsAndFormatsModePanel";
import { APP_VERSION } from "@/config/version";
import { TRAINING_FEEDBACK_WEBHOOK_URL } from "@/config/webhooks";
import { kbQueryDebug } from "@/lib/kb/kbQueryDebug";
import { LeadProvenanceMeta } from "./sections/LeadProvenanceMeta";
import { shouldPromptGhlLocationMismatch } from "@/lib/lead/locationScope";
import type { LeadWorkspaceShellModel } from "@/components/lead/useLeadWorkspaceShellProps";
import { canAccessPrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { LeadSummary } from "@/components/lead/sections/LeadSummary";
import { LeadsModePanel } from "@/components/lead/sections/LeadsModePanel";
import { DevConvoPanel } from "@/components/lead/sections/DevConvoPanel";
import { SalesConsolePanel } from "@/components/lead/sections/SalesConsolePanel";
import { TrainingPanel } from "@/components/lead/sections/TrainingPanel";
import { LeadOwnerTransferPanel } from "@/components/lead/sections/LeadOwnerTransferPanel";
import { LeadSummarySubmitAsDealAction } from "@/components/lead/sections/LeadSummarySubmitAsDealAction";
import type { AdsAssistantPanelIntent } from "@/components/ads-assistant/adsAssistantPanelUtils";

export function LeadWorkspaceShellModes(props: {
  model: LeadWorkspaceShellModel;
  scopedOrganizationId: string | null;
  trainingQuickTab: "roleplay" | "ask" | "contacts" | "reviews" | null;
  setTrainingQuickTab: (v: "roleplay" | "ask" | "contacts" | "reviews" | null) => void;
  adsPanelIntent: AdsAssistantPanelIntent;
  setAdsPanelIntent: (v: AdsAssistantPanelIntent) => void;
  kbEntrySubModeIntent: "default" | "gap_inbox";
  setKbEntrySubModeIntent: (v: "default" | "gap_inbox") => void;
}) {
  const {
    model: m,
    scopedOrganizationId,
    trainingQuickTab,
    setTrainingQuickTab,
    adsPanelIntent,
    setAdsPanelIntent,
    kbEntrySubModeIntent,
    setKbEntrySubModeIntent,
  } = props;

  const opsModePanel = renderLeadWorkspaceOpsMode(m.mode);

  // Container/padding doctrine \u00a7K7: every mode gets the same
  // `<div className="w-full text-left">` wrapper. Removes the silent mobile
  // `text-center` inheritance bug for modes that previously had no wrapper
  // (Dashboard, Admin, Platform, AI Prompting). See
  // `docs/planning/container_padding_pass.md`.
  return (
    <div className="w-full text-left">
      {m.mode === "dashboard" ? (
        <OrgDashboard />
      ) : m.mode === "knowledgebase" ? (
        <KbAdminPanel
          orgId={scopedOrganizationId}
          onLogout={m.devMode ? m.disableDevMode : undefined}
          onNavigateToDevKbRescope={() => {
            m.setMode("dev");
            window.location.hash = "kb-dev-rescope-panel";
          }}
          entrySubModeIntent={kbEntrySubModeIntent}
          onEntrySubModeIntentConsumed={() => setKbEntrySubModeIntent("default")}
        />
      ) : m.mode === "dev" ? (
        <DevPanel />
      ) : m.mode === "admin" ? (
        <AdminPanel
          embedded
          onExit={() => {
            m.setMode("leads");
          }}
        />
      ) : m.mode === "platform" ? (
        <PlatformPanel />
      ) : m.mode === "aiPrompting" ? (
        <AiPromptingPanel />
      ) : m.mode === "adsAssistant" ? (
        <AdsAssistantPanel
          forcedIntent={adsPanelIntent}
          onForcedIntentApplied={() => setAdsPanelIntent(null)}
        />
      ) : m.mode === "adsGuidance" ? (
        <AdsGuidancePanel />
      ) : m.mode === "audienceData" ? (
        <AudienceDataPanel />
      ) : m.mode === "coverageAssistant" ? (
        <CoverageAssistantPanel />
      ) : m.mode === "locationTools" ? (
        <LocationToolsPanel />
      ) : m.mode === "trainingAuthor" ? (
        <TrainingAuthorPanel />
      ) : m.mode === "formsAndFormats" ? (
        <FormsAndFormatsModePanel />
      ) : opsModePanel ? (
        opsModePanel
      ) : m.mode === "training" ? (
        <>
          <TrainingPanel
            salesperson={m.salesperson}
            location={m.location}
            appVersion={APP_VERSION}
            webhookUrl={TRAINING_FEEDBACK_WEBHOOK_URL}
            kbQueryDebug={kbQueryDebug}
            forcedTab={trainingQuickTab}
            onForcedTabApplied={() => setTrainingQuickTab(null)}
          />

          {m.devMode ? (
            <DevConvoPanel appVersion={APP_VERSION} salesperson={m.salesperson} location={m.location} />
          ) : null}
        </>
      ) : m.mode === "salesConsole" ? (
        <SalesConsolePanel
            leads={m.locationScopedLeads}
            organizationId={scopedOrganizationId}
            canEditStickyNote={
              !!m.auth?.session &&
              m.auth.hasRole(
                "trainer",
                "admin",
                "owner",
                "dev",
                "org_admin",
                "org_owner",
                "platform_admin",
                "platform_dev",
                "platform_trainer",
              )
            }
            onBulkImport={m.createLeadsFromBulkTranscript}
            onUpdateFollowUpStatus={m.updateFollowUpStatus}
            onCreateFollowUp={m.createFollowUp}
            onSaveFollowUp={m.saveFollowUp}
            onViewLead={(lead) => {
              m.setMode("leads");
              m.openLeadInEditor(lead);
            }}
          />
      ) : m.mode === "salesIntake" ? (
        <SalesIntakePanel />
      ) : m.mode === "dealerPlaceOrder" ? (
        <DealerPlaceOrderPanel />
      ) : m.mode === "dealerOrderHistory" ? (
        <DealerOrderHistoryPanel />
      ) : m.mode === "opportunities" ? (
        <OpportunitiesModePanel />
      ) : m.mode === "mfgInventory" ? (
        <ManufacturingInventoryPanel
          onOpenInventoryCount={
            m.auth && canAccessPrimaryMode("mfgInventoryCount", m.auth)
              ? () => m.setMode("mfgInventoryCount")
              : undefined
          }
          onOpenPurchasing={
            m.auth && canAccessPrimaryMode("mfgPurchasing", m.auth)
              ? () => m.setMode("mfgPurchasing")
              : undefined
          }
        />
      ) : m.mode === "mfgModels" ? (
        <ManufacturingModelsPanel
          onOpenBomAudit={() => {
            preferMfgAuditsBomLane();
            m.setMode("mfgAudits");
          }}
        />
      ) : m.mode === "mfgPricing" ? (
        <ManufacturingPricingGridPanel />
      ) : m.mode === "mfgOverhead" ? (
        <ManufacturingOverheadBucketsPanel />
      ) : m.mode === "mfgOrders" ? (
        <ManufacturingOrdersPanel />
      ) : m.mode === "mfgPlants" ? (
        <ManufacturingPlantsPanel />
      ) : m.mode === "mfgBuildPipeline" ? (
        <ManufacturingBuildPipelinePanel />
      ) : m.mode === "mfgLaborBilling" ? (
        <ManufacturingLaborBillingPanel />
      ) : m.mode === "mfgFulfill" ? (
        <ManufacturingFulfillPanel />
      ) : m.mode === "mfgAudits" ? (
        <ManufacturingAuditsPanel />
      ) : m.mode === "mfgInventoryCount" ? (
        <ManufacturingInventoryCountPanel />
      ) : m.mode === "mfgPurchasing" ? (
        <ManufacturingPurchasingPanel />
      ) : m.mode === "mfgLaborPay" ? (
        <ManufacturingLaborPayModePanel />
      ) : m.mode === "mfgBuilderAssistant" ? (
        <BuilderAssistantPanel />
      ) : m.mode === "mfgQcDashboard" ? (
        <QcDashboardPanel />
      ) : m.mode === "mfgReports" ? (
        <ManufacturingReportsPanel />
      ) : m.mode === "mfgAdminHome" ? (
        <ManufacturingAdminHomePanel />
      ) : m.mode === "logisticsRoutes" ? (
        <LogisticsRoutesPanel />
      ) : m.mode === "logisticsCalendar" ? (
        <LogisticsCalendarPanel />
      ) : m.mode === "logisticsCarriers" ? (
        <LogisticsCarriersPanel />
      ) : m.mode === "fulfillmentInventory" ? (
        <FulfillmentInventoryPanel />
      ) : m.mode === "fulfillmentOrders" ? (
        <FulfillmentOrdersPanel />
      ) : m.mode === "fulfillmentIntake" ? (
        <FulfillmentIntakePanel />
      ) : m.mode === "fulfillmentForms" ? (
        <FulfillmentFormsPanel />
      ) : m.mode === "fulfillmentEntry" ? (
        <FulfillmentEntryPanel />
      ) : m.mode === "serviceBoard" ? (
        <ServiceBoardPanel />
      ) : m.mode === "serviceRequests" ? (
        <ServiceRequestsPanel />
      ) : m.mode === "serviceScheduling" ? (
        <ServiceSchedulingPanel />
      ) : m.mode === "serviceDefinitions" ? (
        <ServiceDefinitionsPanel />
      ) : m.mode === "serviceMaterialsUsage" ? (
        <ServiceMaterialsUsagePanel />
      ) : (
        <LeadsModePanel
            commitTranscriptToCurrentLead={m.commitTranscriptToCurrentLead}
            devMode={m.devMode}
            devToolsNode={m.devMode ? m.leadsDevToolsNode : null}
            startNewLead={m.startNewLead}
            isListening={m.isListening}
            isAsrReconnecting={m.isAsrReconnecting}
            asrStatus={m.asrStatus}
            stopListening={m.stopListening}
            isStoppingUi={m.isStoppingUi}
            currentLead={m.currentLead}
            transcript={m.transcript}
            showTranscriptDetails={m.showTranscriptDetails}
            setShowTranscriptDetails={m.setShowTranscriptDetails}
            continueListening={m.continueListening}
            closeCurrentLead={m.closeCurrentLead}
            leadView={m.leadView}
            leadSummaryNode={
              m.currentLead?.extracted ? (
                <LeadSummary
                  currentLead={m.currentLead}
                  leads={m.locationScopedLeads}
                  isToday={m.isToday}
                  locationDisplay={m.locationDisplay}
                  leadSourceDisplay={m.leadSourceDisplay}
                  installAddressDisplay={m.installAddressDisplay}
                  lastUpdatedDisplay={m.lastUpdatedDisplay}
                  timelineDisplay={m.timelineDisplay}
                  leadStatusClass={m.leadStatusClass}
                  leadStatusLabel={m.leadStatusLabel}
                  leadAgeDisplay={m.leadAgeDisplay}
                  compactView={m.compactView}
                  setCompactView={m.setCompactView}
                  mergeSuggestion={m.mergeSuggestion}
                  setMergeSuggestion={m.setMergeSuggestion}
                  mergeLeadIntoCurrent={m.mergeLeadIntoCurrent}
                  buddyTags={m.leadSummaryBuddyTags}
                  ghlPayload={m.leadSummaryPayload}
                  newTagInput={m.newTagInput}
                  setNewTagInput={m.setNewTagInput}
                  updateCurrentLeadField={m.updateCurrentLeadField}
                  validationIssues={m.leadSummaryValidationIssues}
                  canSend={m.leadSummaryCanSend}
                  isSending={m.isSending}
                  handleSendClick={m.handleSendClick}
                  reextractCurrentLead={m.reextractCurrentLead}
                  devMode={m.devMode}
                  leadProvenanceNode={
                    m.showLeadsManagerFilters && m.currentLead ? (
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2 dark:border-slate-600 dark:bg-slate-800/60">
                        <LeadProvenanceMeta
                          lead={m.currentLead}
                          ownerLabelByUserId={m.ownerLabelByUserId}
                          locationNameById={m.locationNameById}
                          showOwner={m.showLeadsOwnerFilter}
                        />
                      </div>
                    ) : null
                  }
                  showGhlLocationMismatchHint={
                    !!m.currentLead && shouldPromptGhlLocationMismatch(m.currentLead, m.location)
                  }
                  workspaceLocationLabel={m.location?.name ?? null}
                  leadTransferSection={
                    scopedOrganizationId && m.auth?.session?.user?.id && m.currentLead ? (
                      <LeadOwnerTransferPanel
                        organizationId={scopedOrganizationId}
                        currentUserId={m.auth.session.user.id}
                        lead={m.currentLead}
                        disabled={!m.auth.session}
                        onTransferredAway={m.removeLeadFromWorkspace}
                        setToast={m.setToast}
                      />
                    ) : null
                  }
                  submitAsDealSection={
                    m.currentLead ? (
                      <LeadSummarySubmitAsDealAction
                        lead={m.currentLead}
                        workspaceLocationId={m.auth?.activeLocationId ?? m.location?.id ?? null}
                        onOpenSalesIntake={m.onOpenSalesIntake}
                      />
                    ) : null
                  }
                />
              ) : null
            }
            visibleRecentLeads={m.visibleRecentLeads}
            filteredRecentLeadsCount={m.filteredRecentLeads.length}
            hasMoreLeads={m.hasMoreLeads}
            showAllRecentLeads={m.showAllRecentLeads}
            setShowAllRecentLeads={m.setShowAllRecentLeads}
            recentFilter={m.recentFilter}
            setRecentFilter={m.setRecentFilter}
            recentSort={m.recentSort}
            setRecentSort={m.setRecentSort}
            showLeadsManagerFilters={m.showLeadsManagerFilters}
            showLeadsOwnerFilter={m.showLeadsOwnerFilter}
            recentLocationFilter={m.recentLocationFilter}
            setRecentLocationFilter={m.setRecentLocationFilter}
            recentOwnerFilter={m.recentOwnerFilter}
            setRecentOwnerFilter={m.setRecentOwnerFilter}
            locationOptions={m.locationFilterOptions}
            ownerCandidates={m.ownerCandidates}
            ownerLabelByUserId={m.ownerLabelByUserId}
            locationNameById={m.locationNameById}
            openLeadInEditor={m.openLeadInEditor}
            deleteLead={m.deleteLead}
            mergeLeadIntoCurrent={(id) => void m.mergeLeadIntoCurrent(id)}
            isMergeCandidate={m.isMergeCandidate}
            formatLeadAge={m.formatLeadAge}
            currentLeadId={m.currentLead?.id ?? null}
          />
      )}
    </div>
  );
}
